import {
  Box,
  Table,
  Paper,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  CircularProgress,
  TableContainer,
  Button,
  Chip,
  Typography,
  Link,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import Iconify from "../iconify";
import { format } from "date-fns";
import SeasonDetailsModal from "./SeasonDetailsModal";

export default function VersusSeasonsTable({
  rows,
  loading,
  hasMore,
  onLoadMore,
}) {
  const tableContainerRef = useRef(null);
  const [selectedSeason, setSelectedSeason] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return format(new Date(dateString), "MMM dd, yyyy HH:mm");
    } catch (error) {
      return "Invalid Date";
    }
  };

  const getStatusChip = (season) => {
    const now = new Date();
    const startDate = new Date(season.startsAt);
    const endDate = new Date(season.endsAt);

    if (!season.isActive) {
      return <Chip label="Inactive" color="default" size="small" />;
    }

    if (now < startDate) {
      return <Chip label="Upcoming" color="info" size="small" />;
    } else if (now > endDate || season.nextOpponentUsername === null) {
      return <Chip label="Completed" color="success" size="small" />;
    } else {
      return <Chip label="Ongoing" color="primary" size="small" />;
    }
  };

  const handleViewDetails = (season) => {
    setSelectedSeason(season);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedSeason(null);
  };

  const getUserRank = (userId, members) => {
    const currentUser = members?.find((m) => m.userId === userId);
    if (!currentUser) return "--";

    const wins = currentUser.wins;

    const higherWinsCount = members?.filter((m) => m.wins > wins).length;

    return higherWinsCount + 1;
  };

  const getMvpRank = (userId, seasonStats) => {
    if (!Array.isArray(seasonStats) || seasonStats.length === 0) return "--";

    const userStats = seasonStats.filter((s) => s.userId === userId);
    const userMaxPoints = Math.max(...userStats.map((s) => s.points || 0));

    const higherPointsCount = seasonStats
      .reduce((acc, stat) => {
        const existing = acc.find((s) => s.userId === stat.userId);
        if (!existing) {
          acc.push({ userId: stat.userId, maxPoints: stat.points || 0 });
        } else if (stat.points > existing.maxPoints) {
          existing.maxPoints = stat.points;
        }
        return acc;
      }, [])
      .filter((entry) => entry.maxPoints > userMaxPoints).length;

    return higherPointsCount + 1;
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollContainer = tableContainerRef.current;
      if (!scrollContainer || loading || !hasMore) return;

      const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
      if (scrollTop + clientHeight >= scrollHeight - 10) {
        onLoadMore();
      }
    };

    const scrollContainer = tableContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener("scroll", handleScroll);
      return () => scrollContainer.removeEventListener("scroll", handleScroll);
    }
  }, [loading, hasMore, onLoadMore]);

  return (
    <>
      {loading && rows.length === 0 ? (
        <Box
          sx={{ display: "flex", justifyContent: "center" }}
          style={{ padding: 40 }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer
            component={Paper}
            ref={tableContainerRef}
            sx={{ maxHeight: 600, overflow: "auto" }}
          >
            <Table sx={{ minWidth: 650 }} aria-label="versus seasons table">
              <TableHead>
                <TableRow>
                  <TableCell>Rank</TableCell>
                  <TableCell>Season ID</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Start Date</TableCell>
                  <TableCell>End Date</TableCell>
                  <TableCell>Actions</TableCell>
                  <TableCell>MVP Rank</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows?.map((season, index) => (
                  <TableRow
                    key={season.versusSeasonId || index}
                    sx={{
                      "&:last-child td, &:last-child th": { border: 0 },
                    }}
                  >
                    <TableCell align="center">
                      {getUserRank(season.userId, season.members)}
                    </TableCell>

                    <TableCell component="th" scope="row">
                      {season.versusSeasonId}
                    </TableCell>
                    <TableCell>
                      <Link
                        href={`/dashboard/profile?user_id=${season.userId}`}
                        underline="hover"
                        color="primary"
                        sx={{
                          fontWeight: "medium",
                          cursor: "pointer",
                        }}
                      >
                        {season.username || `User ${season.userId}`}
                      </Link>
                    </TableCell>
                    <TableCell>{getStatusChip(season)}</TableCell>
                    <TableCell>{formatDate(season.createdAt)}</TableCell>
                    <TableCell>{formatDate(season.endsAt)}</TableCell>
                    <TableCell>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Iconify icon="eva:eye-fill" />}
                        onClick={() => handleViewDetails(season)}
                        sx={{ minWidth: "auto" }}
                      >
                        View Details
                      </Button>
                    </TableCell>
                    <TableCell align="center">
                      {getMvpRank(season.userId, season.seasonStats)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {rows.length === 0 && !loading && (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography variant="body1" color="text.secondary">
                No seasons found for this season type.
              </Typography>
            </Box>
          )}

          {loading && rows.length > 0 && (
            <Box sx={{ p: 2, textAlign: "center" }}>
              <CircularProgress size={24} />
            </Box>
          )}
        </>
      )}

      <SeasonDetailsModal
        open={modalOpen}
        onClose={handleCloseModal}
        season={selectedSeason}
        standings={selectedSeason?.members || []}
        cards={selectedSeason?.seasonStats || []}
        userId={selectedSeason?.userId}
      />
    </>
  );
}
